import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../FormInput';
import { dispatchService } from '../../../services/central-api/dispatch';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterSwitch from '../../Filter/components/FilterSwitch';
import { isPlateInvalid } from '../../helpers/plate';
import BasicModal from '../../BasicModal';
import FilterDropdown from '../../Filter/components/FilterDropdown';

interface InvolvedVehicleModalParameters {
    involvedVehicle: InvolvedVehicle;
    setInvolvedVehicle: (value: InvolvedVehicle) => void;
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: () => void;
    actingBodyId: number;
    occurrenceSituation: OccurrenceSituation;
}

export default function InvolvedVehicleModal({ involvedVehicle, setInvolvedVehicle, isModalVisible, setModalVisible, onSubmit, actingBodyId, occurrenceSituation }: InvolvedVehicleModalParameters) {
    const { styles } = useStyles(stylesheet);
    const windowInfo = WindowInformation();

    const [vehicleRelations, setVehicleRelations] = useState<DropdownResource[]>([]);

    async function getVehicleRelations(): Promise<void> {
        try {
            const response = await dispatchService.getSimplifiedVehicleRelations(actingBodyId);
            setVehicleRelations(response);

        } catch (err) {
            console.error(err);
        }
    }

    function isValidForm() {
        if (occurrenceSituation == 'concluded') {
            return false;
        }
        if (involvedVehicle.plate === '' || isPlateInvalid(involvedVehicle.plate)) {
            return false;
        }
        if (involvedVehicle.brand === '') {
            return false;
        }
        if (involvedVehicle.model === '') {
            return false;
        }
        if (involvedVehicle.color === '') {
            return false;
        }
        if (involvedVehicle.chassis === '') {
            return false;
        }
        if (involvedVehicle.vehicleRelationId === 0) {
            return false;
        }
        return true;
    }

    useEffect(() => {
        getVehicleRelations();
    }, []);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('involvedVehicle')}
            width='extra-large'
            primaryButton={{
                text: translate('save'),
                onClick: onSubmit,
                disabled: !isValidForm(),
            }}
        >
            <View style={{ gap: 20, height: 330 }}>
                <View style={styles.row}>
                    <FormInput
                        label={translate('plate')}
                        placeholder={translate('plate')}
                        value={involvedVehicle.plate}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, plate: value.toUpperCase() })}
                        invalid={isPlateInvalid}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        disabled={occurrenceSituation == 'concluded'}
                        required
                    />
                    <FormInput
                        label={translate('brand')}
                        placeholder={translate('brand')}
                        value={involvedVehicle.brand}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, brand: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        disabled={occurrenceSituation == 'concluded'}
                        required
                    />
                    <FormInput
                        label={translate('model')}
                        placeholder={translate('model')}
                        value={involvedVehicle.model}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, model: value })}
                        invalid={() => false}
                        viewStyle={[{ flex: 1 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}
                        disabled={occurrenceSituation == 'concluded'}
                        required
                    />
                </View>
                <View style={[styles.row, { zIndex: 2 }]}>
                    <FormInput
                        label={translate('color')}
                        placeholder={translate('color')}
                        value={involvedVehicle.color}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, color: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        disabled={occurrenceSituation == 'concluded'}
                        required
                    />
                    <FormInput
                        label={translate('chassis')}
                        placeholder={translate('chassis')}
                        value={involvedVehicle.chassis}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, chassis: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }}
                        disabled={occurrenceSituation == 'concluded'}
                        required
                    />
                    <View style={[{ gap: 5, zIndex: 2, flex: 1 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%', columnGap: 0 }]}>
                        <FilterDropdown
                            zIndex={2}
                            label={translate('relation')}
                            items={vehicleRelations.map((item) => {
                                return {
                                    label: item.name,
                                    value: item.id
                                };
                            })}
                            value={involvedVehicle.vehicleRelationId}
                            disabled={occurrenceSituation == 'concluded'}
                            setValue={(value) => {
                                const vehicleRelationId = value(involvedVehicle.vehicleRelationId);
                                setInvolvedVehicle({
                                    ...involvedVehicle,
                                    vehicleRelationId: vehicleRelationId,
                                    vehicleRelation: vehicleRelations.find(vehicleRelation => vehicleRelation.id == vehicleRelationId)
                                });
                            }}
                            searchable={false}
                            required
                        />
                    </View>
                </View>
                <View style={styles.row}>
                    <View style={[windowInfo.isMobileOrTablet ? { width: '100%' } : {}]}>
                        <FilterSwitch
                            value={involvedVehicle.stolen}
                            label={translate('theft')}
                            onChange={(value) => {
                                setInvolvedVehicle({ ...involvedVehicle, stolen: Boolean(value) });
                            }}
                            direction='column'
                            disabled={occurrenceSituation == 'concluded'}
                        />
                    </View>
                    <FormInput
                        label={translate('restriction')}
                        placeholder={translate('restriction')}
                        value={involvedVehicle.restriction || ''}
                        onChangeText={(value) => setInvolvedVehicle({ ...involvedVehicle, restriction: value })}
                        invalid={() => false}
                        viewStyle={windowInfo.isMobileOrTablet ? { width: '100%' } : { flex: 1 }}
                        disabled={occurrenceSituation == 'concluded'}
                    />
                </View>
            </View>
        </BasicModal>
    );
}

const stylesheet = createStyleSheet(() => ({
    row: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: 30,
        minHeight: 'auto',
        rowGap: 20,
    }
}));
